import { Form, Formik } from 'formik';
import { Props, Values } from './NumericalSeriesForm.types';
import * as Yup from 'yup';
import FormField from '../../components/field/FormField';
import Button from '../../components/button/Button';
import { useEffect, useState } from 'react';
import http from '../../api/axios';
import { apiUrl } from '../../api/apiUrl';
import { DocumentType, DocumentTypeTranslated } from '../../types/Invoice';
import { Loader } from '../../components/loader/Loader';
import { convertEnumToArray, ObjectFromEnum } from '../../utils/functions/data.functions';
import { useTranslation } from '../../context/translationContext';

export const NumericalSeriesForm = ({ onSubmit, initialValues, onBack }: Props) => {
  const { t } = useTranslation();
  const [supportedSymbols, setSupportedSymbols] = useState<string[] | null>(null);
  const invoiceOptions = convertEnumToArray(DocumentType, DocumentTypeTranslated, t);
  /**
   * Load supported symbols for numerical series
   */
  const fetchSupportedSymbols = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.NUMERICAL_SERIES_SUPPORTED_SYMBOLS}`,
    });
    setSupportedSymbols(response?.data);
  };

  useEffect(() => {
    fetchSupportedSymbols();
  }, []);

  const _initialValues: Values = {
    name: '',
    documentType: DocumentType.INVOICE,
    format: '{YYYY}{C:4}',
    formatVariableSymbol: '{YYYY}{C:4}',
    counterForEachMonth: false,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required.'),
    format: Yup.string().required('Format is required.'),
    formatVariableSymbol: Yup.string().required('Format for variable symbol is required.'),
    documentType: Yup.string().required('Document type is required.'),
  });

  if (!supportedSymbols) return <Loader />;
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues || _initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ errors, touched, setFieldValue, values }) => (
        <Form className="w-full">
          <div className="my-4">
            <FormField
              id="name"
              name="name"
              label={t('page.numerical-serie.input-name')}
              errors={errors}
              touched={touched}
            />
          </div>
          <div className="my-4">
            <FormField
              id="format"
              name="format"
              label={t('page.numerical-serie.input-format')}
              errors={errors}
              touched={touched}
              helperText={
                <>
                  <span>{t('page.numerical-serie.supported-symbols')} </span>
                  {supportedSymbols.map((s: string, index: number) => (
                    <span key={index}>{s}</span>
                  ))}
                </>
              }
            />
          </div>
          <div className="my-4">
            <FormField
              id="formatVariableSymbol"
              name="formatVariableSymbol"
              label={t('page.numerical-serie.input-format-vs')}
              errors={errors}
              touched={touched}
              helperText={
                <>
                  <span>{t('page.numerical-serie.supported-symbols')} </span>
                  {supportedSymbols.map((s: string, index: number) => (
                    <span key={index}>{s}</span>
                  ))}
                </>
              }
            />
          </div>
          <div className="my-4">
            <FormField
              id={'select'}
              type="select"
              name="documentType"
              onChange={(e) => {
                if (!e.target.value) return;
                setFieldValue('documentType', e.target.value);
              }}
            >
              {invoiceOptions.map((opt: ObjectFromEnum, index: number) => (
                <option key={index} value={opt.value}>
                  {opt.name}
                </option>
              ))}
            </FormField>
          </div>
          <div className="my-4 flex flex-row items-center">
            <label htmlFor={'counterForEachMonth'} className="mr-5">
              {t('page.numerical-serie.col-counter-each-month')}
            </label>

            <FormField
              name={'counterForEachMonth'}
              id={'counterForEachMonth'}
              type="checkbox"
              checked={values?.counterForEachMonth}
              className={`w-4 h-4`}
            />
          </div>
          <div className="flex float-left">
            <Button title={t('general.back')} color="gray" className="mr-3" onClick={onBack} />
            <Button title={t('general.save')} color="green" type="submit" showLoading />
          </div>
        </Form>
      )}
    </Formik>
  );
};
